import { useLayoutEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'
import { PluginKey } from '@tiptap/pm/state'
import { CheckSquare, FileText, FolderOpen } from 'lucide-react'
import {  getEntitySuggestions } from './useEntityLinkSuggestions'
import { EntityLinkChip } from './EntityLinkChip'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import type { LinkableEntityType } from '@/lib/constants/entityTypes'
import type {EntityLinkSuggestionItem} from './useEntityLinkSuggestions';
import { cn } from '@/lib/utils'
import { ENTITY_TYPES, LINKABLE_ENTITY_LABELS } from '@/lib/constants/entityTypes'

const ENTITY_TYPE_ICONS: Record<string, typeof FileText> = {
  [ENTITY_TYPES.WORK_ITEM]: CheckSquare,
  [ENTITY_TYPES.DOCUMENT]: FileText,
  [ENTITY_TYPES.PROJECT]: FolderOpen,
}

function EntityLinkMenu({
  items,
  selectedIndex,
  onSelect,
  onHover,
}: {
  items: Array<EntityLinkSuggestionItem>
  selectedIndex: number
  onSelect: (index: number) => void
  onHover: (index: number) => void
}) {
  // Group items by entity type for display
  const grouped = items.reduce(
    (acc, item, originalIndex) => {
      const label =
        LINKABLE_ENTITY_LABELS[item.entityType as LinkableEntityType] ?? item.entityType
      if (!acc[label]) acc[label] = []
      acc[label].push({ item, originalIndex })
      return acc
    },
    {} as Record<string, Array<{ item: EntityLinkSuggestionItem; originalIndex: number }>>,
  )

  return (
    <div
      className={cn(
        'z-50 min-w-[280px] max-w-[360px] overflow-hidden rounded-lg border border-border bg-popover shadow-lg',
        'animate-in fade-in-0 zoom-in-95',
      )}
    >
      {items.length === 0 ? (
        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
          No results found
        </div>
      ) : (
        <div className="max-h-[300px] overflow-y-auto p-1">
          {Object.entries(grouped).map(([label, group]) => (
            <div key={label}>
              <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {label}
              </p>
              {group.map(({ item, originalIndex }) => {
                const Icon = ENTITY_TYPE_ICONS[item.entityType] ?? FileText
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                      selectedIndex === originalIndex
                        ? 'bg-secondary text-foreground'
                        : 'text-foreground hover:bg-accent',
                    )}
                    onMouseEnter={() => onHover(originalIndex)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSelect(originalIndex)}
                  >
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    <div className="flex flex-1 items-center gap-1.5 overflow-hidden">
                      {item.icon && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          {item.icon}
                        </span>
                      )}
                      <span className="truncate font-medium">{item.label}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EntityLinkMenuWrapper(props: SuggestionProps<EntityLinkSuggestionItem>) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const handleSelect = (index: number) => {
    const item = props.items[index]
    if (item) {
      props.command({
        id: item.id,
        label: item.label,
        entityType: item.entityType,
        icon: item.icon,
      })
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp') {
      setSelectedIndex((prev) =>
        prev <= 0 ? props.items.length - 1 : prev - 1,
      )
      return true
    }
    if (event.key === 'ArrowDown') {
      setSelectedIndex((prev) =>
        prev >= props.items.length - 1 ? 0 : prev + 1,
      )
      return true
    }
    if (event.key === 'Enter') {
      handleSelect(selectedIndex)
      return true
    }
    return false
  }

  const wrapperRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = wrapperRef.current
    if (el) {
      ;(el as any).__onKeyDown = onKeyDown
    }
  })

  useLayoutEffect(() => {
    setSelectedIndex(0)
  }, [props.items])

  return (
    <div ref={wrapperRef}>
      <EntityLinkMenu
        items={props.items}
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
        onHover={setSelectedIndex}
      />
    </div>
  )
}

const entityLinkSuggestion: Omit<SuggestionOptions<EntityLinkSuggestionItem>, 'editor'> = {
  char: '[[',
  allowSpaces: true,
  command: ({ editor, range, props: attrs }) => {
    // Delete the trigger text and insert the entityLink node
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent([
        {
          type: 'entityLink',
          attrs: {
            id: attrs.id,
            entityType: attrs.entityType,
            label: attrs.label,
            icon: attrs.icon,
          },
        },
        { type: 'text', text: ' ' },
      ])
      .run()
  },
  items: ({ query }) => {
    const lower = query.trim().toLowerCase()
    const all = getEntitySuggestions()
    if (!lower) return all.slice(0, 20)
    return all
      .filter(
        (item) =>
          item.label.toLowerCase().includes(lower) ||
          (item.icon && item.icon.toLowerCase().includes(lower)),
      )
      .slice(0, 20)
  },
  render: () => {
    let container: HTMLDivElement | null = null
    let root: ReturnType<typeof createRoot> | null = null

    return {
      onStart: (props) => {
        container = document.createElement('div')
        container.style.position = 'absolute'
        container.style.zIndex = '100'

        if (props.clientRect) {
          const rect = props.clientRect()
          if (rect) {
            container.style.left = `${rect.left}px`
            container.style.top = `${rect.bottom}px`
          }
        }

        document.body.appendChild(container)
        root = createRoot(container)
        root.render(<EntityLinkMenuWrapper {...props} />)
      },
      onUpdate: (props) => {
        if (container && props.clientRect) {
          const rect = props.clientRect()
          if (rect) {
            container.style.left = `${rect.left}px`
            container.style.top = `${rect.bottom}px`
          }
        }
        root?.render(<EntityLinkMenuWrapper {...props} />)
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          root?.unmount()
          container?.remove()
          container = null
          root = null
          return true
        }
        const el = container?.firstElementChild
        if (el && (el as any).__onKeyDown) {
          return (el as any).__onKeyDown(props.event)
        }
        return false
      },
      onExit: () => {
        root?.unmount()
        container?.remove()
        container = null
        root = null
      },
    }
  },
}

/**
 * Custom Tiptap node for entity links (pages, tasks, projects).
 * Triggered by typing `[[` in the editor.
 */
export const EntityLinkExtension = Node.create({
  name: 'entityLink',
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-id'),
        renderHTML: (attributes) => ({
          'data-entity-id': attributes.id,
        }),
      },
      entityType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-entity-type'),
        renderHTML: (attributes) => ({
          'data-entity-type': attributes.entityType,
        }),
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => ({
          'data-label': attributes.label,
        }),
      },
      icon: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-icon'),
        renderHTML: (attributes) => ({
          'data-icon': attributes.icon,
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-entity-link]',
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-entity-link': '',
        },
        HTMLAttributes,
      ),
      node.attrs.label ?? node.attrs.id,
    ]
  },

  // Live chip in the editor; renderHTML above stays the serialised form
  // (and matches mobile's parseHTML selector).
  addNodeView() {
    return ReactNodeViewRenderer(EntityLinkChip, { as: 'span' })
  },

  renderText({ node }) {
    return `[[${node.attrs.label ?? node.attrs.id}]]`
  },

  addKeyboardShortcuts() {
    return {}
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...entityLinkSuggestion,
        pluginKey: new PluginKey('entityLinkSuggestion'),
      }),
    ]
  },
})
